/**
 * useEmailEvents Hook
 *
 * React hook for managing email events with real-time Firestore updates.
 * Provides state management for listing, creating, updating, and deleting email event configurations.
 */

import { notifications } from "@mantine/notifications";
import { useEffect, useState, useCallback } from "react";
import type { EmailEventType } from "../../../../../../types/email-event.d";
import { subscribeToEmailEvents } from "../firestore";
import { createEmailEvent, updateEmailEvent, deleteEmailEvent } from "../functions";
import type { EmailEventWithId, EmailEventFormData } from "../schema/email-event.types";

/**
 * Hook state interface
 */
interface UseEmailEventsResult {
  /** Array of email events */
  events: EmailEventWithId[];
  /** Loading state (initial load) */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Refresh events manually */
  refresh: () => void;
  /** Create new email event */
  createEvent: (data: EmailEventFormData) => Promise<void>;
  /** Update existing email event */
  updateEvent: (eventType: EmailEventType, updates: Partial<Omit<EmailEventFormData, "event_type">>) => Promise<void>;
  /** Delete email event */
  deleteEvent: (eventType: EmailEventType) => Promise<void>;
  /** Toggle event enabled/disabled */
  toggleEvent: (eventType: EmailEventType, enabled: boolean) => Promise<void>;
}

/**
 * useEmailEvents Hook
 *
 * Manages email event configurations for the current licenciada with real-time updates.
 * @param licenciadaId - Licenciada ID to fetch events for
 * @param onlyEnabled - Filter only enabled events (default: false)
 * @returns Hook result with events, loading state, and actions
 * @example
 * ```tsx
 * function EmailEventsList() {
 *   const { events, loading, createEvent, updateEvent, deleteEvent } = useEmailEvents("lic123");
 *
 *   if (loading) return <Loader />;
 *
 *   return (
 *     <div>
 *       {events.map(event => (
 *         <div key={event.id}>{event.event_type}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEmailEvents(licenciadaId: string, onlyEnabled: boolean = false): UseEmailEventsResult {
  const [events, setEvents] = useState<EmailEventWithId[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!licenciadaId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToEmailEvents(
      licenciadaId,
      (newEvents) => {
        setEvents(newEvents);
        setLoading(false);
      },
      onlyEnabled
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [licenciadaId, onlyEnabled, refreshTrigger]);

  // Manual refresh
  const refresh = useCallback(() => {
    setRefreshTrigger((previous) => previous + 1);
  }, []);

  // Create new email event
  const createEvent = useCallback(
    async (data: EmailEventFormData): Promise<void> => {
      try {
        await createEmailEvent(licenciadaId, data);

        notifications.show({
          title: "Evento criado",
          message: `Evento de email "${data.event_type}" criado com sucesso`,
          color: "green"
        });
      } catch (error_) {
        console.error("Failed to create email event:", error_);

        const errorMessage = error_ instanceof Error ? error_.message : "Erro ao criar evento de email";

        notifications.show({
          title: "Erro",
          message: errorMessage,
          color: "red"
        });

        throw error_;
      }
    },
    [licenciadaId]
  );

  // Update existing email event
  const updateEvent = useCallback(
    async (eventType: EmailEventType, updates: Partial<Omit<EmailEventFormData, "event_type">>): Promise<void> => {
      try {
        await updateEmailEvent(licenciadaId, eventType, updates);

        notifications.show({
          title: "Evento atualizado",
          message: `Evento "${eventType}" atualizado com sucesso`,
          color: "green"
        });
      } catch (error_) {
        console.error("Failed to update email event:", error_);

        const errorMessage = error_ instanceof Error ? error_.message : "Erro ao atualizar evento de email";

        notifications.show({
          title: "Erro",
          message: errorMessage,
          color: "red"
        });

        throw error_;
      }
    },
    [licenciadaId]
  );

  // Delete email event
  const deleteEvent = useCallback(
    async (eventType: EmailEventType): Promise<void> => {
      try {
        await deleteEmailEvent(licenciadaId, eventType);

        notifications.show({
          title: "Evento removido",
          message: `Evento "${eventType}" removido com sucesso`,
          color: "green"
        });
      } catch (error_) {
        console.error("Failed to delete email event:", error_);

        const errorMessage = error_ instanceof Error ? error_.message : "Erro ao remover evento de email";

        notifications.show({
          title: "Erro",
          message: errorMessage,
          color: "red"
        });

        throw error_;
      }
    },
    [licenciadaId]
  );

  // Toggle event enabled/disabled
  const toggleEvent = useCallback(
    async (eventType: EmailEventType, enabled: boolean): Promise<void> => {
      try {
        await updateEmailEvent(licenciadaId, eventType, { enabled });

        const statusText = enabled ? "ativado" : "desativado";

        notifications.show({
          title: "Status atualizado",
          message: `Evento "${eventType}" ${statusText}`,
          color: "green"
        });
      } catch (error_) {
        console.error("Failed to toggle email event:", error_);

        const errorMessage = error_ instanceof Error ? error_.message : "Erro ao alterar status do evento";

        notifications.show({
          title: "Erro",
          message: errorMessage,
          color: "red"
        });

        throw error_;
      }
    },
    [licenciadaId]
  );

  return {
    events,
    loading,
    error,
    refresh,
    createEvent,
    updateEvent,
    deleteEvent,
    toggleEvent
  };
}
